import type {
  PlanogramTemplate,
  TemplateFilterParams,
  TemplateStatus,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  BatchOperationResult,
  PlanPreview
} from '../types/plan-template';

import type {
  PaginationParams,
  PaginatedResponse,
  ApiResponse,
} from '../types/index';

import { MOCK_PLANOGRAM_TEMPLATES } from '../mock/planogramTemplateData';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import { generateFirstExecutionTimestamp, getCurrentTimestamp, getTomorrowDateString } from '../utils/dateTimeUtil';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

// Simulate API delay
const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Generate template code
const generateTemplateCode = () => {
  const existingCodes = MOCK_PLANOGRAM_TEMPLATES.map(t => t.code);
  let newCode: string;
  let counter = MOCK_PLANOGRAM_TEMPLATES.length + 1;
  
  do {
    newCode = `TPL${String(counter).padStart(5, '0')}`;
    counter++;
  } while (existingCodes.includes(newCode));
  
  return newCode;
};

// In-memory storage for templates (simulating database)
let templatesStorage = [...MOCK_PLANOGRAM_TEMPLATES];

export class PlanogramTemplateService {
  // 静态配置，控制是否使用 mock 数据
  private static useMock: boolean = true;
  
  /**
   * 设置是否使用 mock 数据
   * @param useMock 是否使用 mock 数据
   */
  public static setUseMock(useMock: boolean): void {
    PlanogramTemplateService.useMock = useMock;
  }
  
  /**
   * 获取当前是否使用 mock 数据
   */
  public static getUseMock(): boolean {
    return PlanogramTemplateService.useMock;
  }

  /**
   * Get paginated template list with filtering
   */
  static async getTemplateList(
    filters: TemplateFilterParams = {},
    pagination: PaginationParams = { current: 1, pageSize: 20 }
  ): Promise<ApiResponse<PaginatedResponse<PlanogramTemplate>>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        // Apply filters
        let filteredTemplates = [...templatesStorage];
        
        // Filter by search text
        if (filters.searchText) {
          const searchText = filters.searchText.toLowerCase();
          filteredTemplates = filteredTemplates.filter(template => 
            template.code.toLowerCase().includes(searchText) ||
            template.name.toLowerCase().includes(searchText) ||
            template.description?.toLowerCase().includes(searchText)
          );
        }
        
        // Filter by plan type
        if (filters.planType && filters.planType !== 'all') {
          filteredTemplates = filteredTemplates.filter(template => 
            template.planType === filters.planType
          );
        }
        
        // Filter by status
        if (filters.status && filters.status !== 'all') {
          filteredTemplates = filteredTemplates.filter(template => 
            template.status === filters.status
          );
        }
        
        // Filter by creator
        if (filters.creator) {
          filteredTemplates = filteredTemplates.filter(template => 
            template.creator === filters.creator
          );
        }
        
        // Filter by next generation time range
        if (filters.nextGenerationTimeStart || filters.nextGenerationTimeEnd) {
          filteredTemplates = filteredTemplates.filter(template => {
            const nextGenTime = template.executionCycle?.nextGenerationTime;
            if (!nextGenTime) return false;
            
            if (filters.nextGenerationTimeStart && nextGenTime < filters.nextGenerationTimeStart) {
              return false;
            }
            
            if (filters.nextGenerationTimeEnd && nextGenTime > filters.nextGenerationTimeEnd) {
              return false;
            }
            
            return true;
          });
        }
        
        // Sort by update time (newest first by default)
        filteredTemplates.sort((a, b) => {
          const dateA = a.updateTime || a.createTime;
          const dateB = b.updateTime || b.createTime;
          return dateB - dateA;
        });
        
        // Apply pagination
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedTemplates = filteredTemplates.slice(startIndex, endIndex);
        
        return createPaginatedResponse(
          paginatedTemplates,
          pagination.current,
          pagination.pageSize,
          filteredTemplates.length
        );
      } else {
        // 真实 API 请求
        const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.PAGE, {
          ...filters,
          currentPage: pagination.current,
          pageSize: pagination.pageSize
        });
        
        return response.data;
      }
    } catch (error) {
      console.error('Failed to fetch template list:', error);
      return createErrorResponse('Failed to fetch template list');
    }
  }

  /**
   * Get template by ID
   */
  static async getTemplateById(id: string): Promise<ApiResponse<PlanogramTemplate>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        const template = templatesStorage.find(t => t.id === id);
        
        if (!template) {
          return createErrorResponse('Template not found', 'NOT_FOUND');
        }
        
        return createSuccessResponse(template);
      } else {
        // 真实 API 请求
        const response = await httpClient.get(`${API_ENDPOINTS.TEMPLATES.DETAIL}/${id}`);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to fetch template details:', error);
      return createErrorResponse('Failed to fetch template details');
    }
  }

  /**
   * Create new template
   */
  static async createTemplate(request: CreateTemplateRequest): Promise<ApiResponse<PlanogramTemplate>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        // Check if code already exists
        if (request.code && templatesStorage.some(t => t.code === request.code)) {
          return createErrorResponse('Template code already exists', 'DUPLICATE_CODE');
        }
        
        // Generate new template
        const newTemplate: PlanogramTemplate = {
          id: generateId(),
          code: request.code || generateTemplateCode(),
          name: request.name,
          description: request.description || '',
          planType: request.planType,
          status: 'Draft',
          creator: 'Current User',
          ownerName: 'Current User',
          createTime: getCurrentTimestamp(),
          updateTime: getCurrentTimestamp(),
          // Add other required fields with defaults
          businessActivity: request.businessActivity || '',
          owner: request.owner || '',
          milestones: request.milestones || [],
          executionCycle: request.executionCycle || {
            frequencyType: 'Monthly',
            interval: 1,
            firstExecutionDate: generateFirstExecutionTimestamp(getTomorrowDateString()),
            terminationType: 'Infinite Repeat',
            cycleStatus: 'Active'
          },
          scopeDefinition: request.scopeDefinition || {
            scopeType: 'All Stores',
            storeGroupList: [],
            customStoreList: [],
            storeSelectionCriteria: {},
            excludedStores: [],
            storeCount: 0,
            scopeValidationStatus: 'Not Validated',
            lastScopeUpdate: getCurrentTimestamp()
          },
          validationStatus: 'Not Validated',
          usageStatistics: {
            usageCount: 0,
            activePlans: 0,
            usageFeedback: [],
            performanceMetrics: {
              averageExecutionTime: 0,
              onTimeCompletionRate: 0,
              resourceUtilization: 0,
              errorRate: 0
            }
          },
          remark: request.remark
        };
        
        // Add to storage
        templatesStorage.push(newTemplate);
        
        return createSuccessResponse(newTemplate);
      } else {
        // 真实 API 请求
        const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.CREATE, request);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      return createErrorResponse('Failed to create template');
    }
  }

  /**
   * Update existing template
   */
  static async updateTemplate(id: string, request: UpdateTemplateRequest): Promise<ApiResponse<PlanogramTemplate>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        // Find template
        const templateIndex = templatesStorage.findIndex(t => t.id === id);
        
        if (templateIndex === -1) {
          return createErrorResponse('Template not found', 'NOT_FOUND');
        }
        
        const template = templatesStorage[templateIndex];
        
        // Check if template is in use
        if (template.usageStatistics.activePlans > 0) {
          return createErrorResponse('Cannot update template that is in use', 'TEMPLATE_IN_USE');
        }
        
        // For published templates, only certain fields can be updated
        if (template.status === 'Published') {
          // Only allow updating description and certain non-structural fields
          const updatedTemplate = {
            ...template,
            description: request.description || template.description,
            updateTime: getCurrentTimestamp()
          };
          
          templatesStorage[templateIndex] = updatedTemplate;
          return createSuccessResponse(updatedTemplate);
        }
        
        // For draft templates, all fields can be updated
        const updatedTemplate = {
          ...template,
          ...request,
          updateTime: getCurrentTimestamp()
        };
        
        templatesStorage[templateIndex] = updatedTemplate;
        return createSuccessResponse(updatedTemplate);
      } else {
        // 真实 API 请求
        request.id = id;
        const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.UPDATE, request);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to update template:', error);
      return createErrorResponse('Failed to update template');
    }
  }

  static async updateTemplateBasicInfo(id: string, request: UpdateTemplateRequest): Promise<ApiResponse<PlanogramTemplate>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        // Find template
        const templateIndex = templatesStorage.findIndex(t => t.id === id);
        
        if (templateIndex === -1) {
          return createErrorResponse('Template not found', 'NOT_FOUND');
        }
        
        const template = templatesStorage[templateIndex];
        
        // Check if template is in use
        if (template.usageStatistics.activePlans > 0) {
          return createErrorResponse('Cannot update template that is in use', 'TEMPLATE_IN_USE');
        }
        
        // For published templates, only certain fields can be updated
        if (template.status === 'Published') {
          // Only allow updating description and certain non-structural fields
          const updatedTemplate = {
            ...template,
            description: request.description || template.description,
            updateTime: getCurrentTimestamp()
          };
          
          templatesStorage[templateIndex] = updatedTemplate;
          return createSuccessResponse(updatedTemplate);
        }
        
        // For draft templates, all fields can be updated
        const updatedTemplate = {
          ...template,
          ...request,
          updateTime: getCurrentTimestamp()
        };
        
        templatesStorage[templateIndex] = updatedTemplate;
        return createSuccessResponse(updatedTemplate);
      } else {
        // 真实 API 请求
        request.id = id;
        const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.UPDATE_BASIC, request);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to update template:', error);
      return createErrorResponse('Failed to update template');
    }
  }

  /**
   * Delete template
   */
  static async deleteTemplate(id: string): Promise<ApiResponse<boolean>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay(400);

        const templateIndex = templatesStorage.findIndex(t => t.id === id);
        
        if (templateIndex === -1) {
          return {
            traceId: generateId(),
            success: false,
            errorMessage: '模板不存在',
            flag: 'fail',
            data: false,
            message: '模板不存在',
            errorCode: 'TEMPLATE_NOT_FOUND'
          };
        }

        const template = templatesStorage[templateIndex];

        // Check if template is in use
        if (template.usageStatistics.activePlans > 0) {
          return {
            traceId: generateId(),
            success: false,
            errorMessage: '模板正在使用中，无法删除',
            flag: 'fail',
            data: false,
            message: '模板正在使用中，无法删除',
            errorCode: 'TEMPLATE_IN_USE'
          };
        }

        templatesStorage.splice(templateIndex, 1);

        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: true,
          message: '模板删除成功'
        };
      } else {
        // 真实 API 请求
        const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.DELETE,{id});
        return response.data;
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '删除模板失败',
        flag: 'fail',
        data: false,
        message: '删除模板失败',
        errorCode: 'TEMPLATE_DELETE_ERROR'
      };
    }
  }


  /**
   * Preview generated plans
   */
  // static async previewGeneratedPlans(templateId: string): Promise<ApiResponse<PlanPreview[]>> {
  //   try {
  //     if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
  //       await delay();
        
  //       const template = templatesStorage.find(t => t.id === templateId);
  //       if (!template) {
  //         return {
  //           traceId: generateId(),
  //           success: false,
  //           errorMessage: '模板不存在',
  //           flag: 'fail',
  //           data: [],
  //           message: '模板不存在',
  //           errorCode: 'TEMPLATE_NOT_FOUND'
  //         };
  //       }

  //       // 模拟生成计划预览
  //       const plans: PlanPreview[] = [];
  //       const cycleConfig = template.executionCycle;
        
  //       // 根据周期配置生成预览
  //       if (cycleConfig.frequencyType === 'Monthly' || cycleConfig.frequencyType === 'Yearly') {
  //         const startDate = new Date(cycleConfig.firstExecutionDate);
  //         const endDate = cycleConfig.terminationType === 'End on Date' 
  //           ? new Date(cycleConfig.endDate!) 
  //           : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 默认一年

  //         let currentDate = new Date(startDate);
  //         let planIndex = 1;

  //         while (currentDate <= endDate && plans.length < 10) { // 最多预览10个计划
  //           plans.push({
  //             id: generateId(),
  //             name: `${template.name} - 第${planIndex}期`,
  //             scheduledDate: currentDate.toISOString().split('T')[0],
  //             estimatedDuration: template.milestones.length * 7, // 估算每个里程碑7天
  //             affectedStores: template.scopeDefinition.storeGroupList?.length || 0,
  //             milestoneCount: template.milestones.length
  //           });

  //           // 计算下一个周期
  //           if (cycleConfig.frequencyType === 'Monthly') {
  //             currentDate.setMonth(currentDate.getMonth() + 1);
  //           } else {
  //             currentDate.setMonth(currentDate.getMonth() + 12);
  //           }
  //           planIndex++;
  //         }
  //       }

  //       return {
  //         traceId: generateId(),
  //         success: true,
  //         errorMessage: null,
  //         flag: 'success',
  //         data: plans,
  //         message: '预览生成成功'
  //       };
  //     } else {
  //       // 真实 API 请求
  //       const response = await httpClient.get(`${API_ENDPOINTS.TEMPLATES.PREVIEW_PLANS}?templateId=${templateId}`);
  //       return response.data;
  //     }
  //   } catch (error) {
  //     console.error('Failed to preview generated plans:', error);
  //     return {
  //       traceId: generateId(),
  //       success: false,
  //       errorMessage: '预览生成失败',
  //       flag: 'fail',
  //       data: [],
  //       message: '预览生成失败',
  //       errorCode: 'PREVIEW_ERROR'
  //     };
  //   }
  // }

  /**
   * Batch update template status
   */
  static async batchUpdateStatus(
    ids: string[],
    newStatus: TemplateStatus
  ): Promise<ApiResponse<BatchOperationResult>> {
    try {
      if (PlanogramTemplateService.useMock && WHOLE_USE_MOCK) {
        await delay(800);

        const results: BatchOperationResult = {
          successCount: 0,
          failureCount: 0,
          errors: []
        };

        for (const id of ids) {
          const templateIndex = templatesStorage.findIndex(t => t.id === id);
          
          if (templateIndex === -1) {
            results.failureCount++;
            results.errors.push({
              id,
              error: '模板不存在'
            });
            continue;
          }

          const template = templatesStorage[templateIndex];

          // 检查是否可以更新状态
          if (newStatus === 'Published') {
            // 检查是否可以启用
            if (template.status !== 'Disabled' && template.status !== 'Draft') {
              results.failureCount++;
              results.errors.push({
                id,
                error: '只有禁用或草稿状态的模板可以启用'
              });
              continue;
            }
          } else if (newStatus === 'Disabled') {
            // 检查是否可以禁用
            if (template.status !== 'Published') {
              results.failureCount++;
              results.errors.push({
                id,
                error: '只有已发布状态的模板可以禁用'
              });
              continue;
            }
          }

          // 更新状态
          templatesStorage[templateIndex] = {
            ...template,
            status: newStatus,
            updateTime: getCurrentTimestamp()
          };

          results.successCount++;
        }

        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: results,
          message: `批量${newStatus === 'Published' ? '启用' : '禁用'}完成：成功 ${results.successCount} 个，失败 ${results.failureCount} 个`
        };
      } else {
        // 真实 API 请求
        if ("Published" === newStatus) {
          const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.BATCH_ENABLE, {
            ids,
            status: newStatus
          });
          return response.data;
        } else {
          const response = await httpClient.post(API_ENDPOINTS.TEMPLATES.BATCH_DISABLE, {
            ids,
            status: newStatus
          });
          return response.data;
        }
        
      }
    } catch (error) {
      console.error('Failed to batch update status:', error);
      return {
        traceId: generateId(),
        success: false,
        errorMessage: `批量${newStatus === 'Published' ? '启用' : '禁用'}失败`,
        flag: 'fail',
        data: {
          successCount: 0,
          failureCount: ids.length,
          errors: ids.map(id => ({ id, error: `${newStatus === 'Published' ? '启用' : '禁用'}失败` }))
        },
        message: `批量${newStatus === 'Published' ? '启用' : '禁用'}失败`,
        errorCode: `BATCH_${newStatus === 'Published' ? 'ENABLE' : 'DISABLE'}_ERROR`
      };
    }
  }
}

export default PlanogramTemplateService;